import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '@/context/StoreContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { AuthDialog } from '@/components/auth/AuthDialog';
import { Seo } from '@/components/Seo';
import { Check, Loader2, MapPin, UserCheck } from 'lucide-react';

const field = 'w-full bg-transparent border border-nw-gold/25 rounded-[3px] px-3 h-11 text-nw-ivory placeholder:text-nw-warmgrey/50 focus:outline-none focus:border-nw-gold text-sm';
const label = 'block text-[10px] tracking-luxe uppercase text-nw-warmgrey mb-2';
const EMPTY_ADDR = { line1: '', line2: '', city: '', postal_code: '', country: '', state: '' };

export default function Checkout() {
  const { t, lang, cart, formatPrice, cartSubtotal, customer, hydrateCart } = useStore();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [methods, setMethods] = useState({});
  const [shipping, setShipping] = useState('standard');
  const [insurance, setInsurance] = useState(true);
  const [pricing, setPricing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [address, setAddress] = useState(EMPTY_ADDR);
  const [selectedAddr, setSelectedAddr] = useState(null); // saved address id or 'new'
  const [saveAddress, setSaveAddress] = useState(true);

  useEffect(() => { if (cart.length === 0) navigate('/cart'); }, [cart.length]); // eslint-disable-line
  useEffect(() => { hydrateCart(); }, []); // eslint-disable-line
  useEffect(() => { api.shippingMethods().then((d) => setMethods(d.methods)).catch(() => {}); }, []);

  // Prefill from the signed-in account (name, e-mail, default address)
  useEffect(() => {
    if (!customer) return;
    const [first, ...rest] = (customer.full_name || '').split(' ');
    setCustomerInfo((c) => ({
      first_name: c.first_name || first || '', last_name: c.last_name || rest.join(' ') || '',
      email: c.email || customer.email || '', phone: c.phone || customer.phone || '',
    }));
    const saved = customer.addresses || [];
    if (saved.length && selectedAddr === null) {
      const def = saved.find((a) => a.is_default) || saved[0];
      pickSaved(def);
    }
  }, [customer]); // eslint-disable-line

  useEffect(() => {
    if (cart.length === 0) return;
    api.priceCart({ items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })), shipping_method: shipping, insurance })
      .then(setPricing).catch(() => {});
  }, [shipping, insurance, cart]);

  const pickSaved = (a) => {
    setSelectedAddr(a.id);
    setAddress({ line1: a.line1, line2: a.line2 || '', city: a.city, postal_code: a.postal_code, country: a.country, state: a.state || '' });
    setSaveAddress(false);
  };
  const pickNew = () => { setSelectedAddr('new'); setAddress(EMPTY_ADDR); setSaveAddress(true); };

  const steps = t.checkout.steps;
  const setC = (k) => (e) => setCustomerInfo((c) => ({ ...c, [k]: e.target.value }));
  const setA = (k) => (e) => setAddress((a) => ({ ...a, [k]: e.target.value }));

  const canNext = () => {
    if (step === 0) return customerInfo.first_name && customerInfo.last_name && customerInfo.email;
    if (step === 1) return address.line1 && address.city && address.postal_code && address.country;
    return true;
  };

  const launchPayment = async () => {
    setSubmitting(true);
    try {
      const res = await api.checkout({
        items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: customerInfo, delivery_address: address, shipping_method: shipping, insurance,
        origin_url: window.location.origin, lang, save_address: selectedAddr !== 'new' ? false : saveAddress,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.common.error);
      setSubmitting(false);
    }
  };

  // A visitor without an account is asked to create one (or sign in) before the payment starts.
  const pay = () => { if (!customer) { setAuthOpen(true); return; } launchPayment(); };

  return (
    <div className="bg-nw-black">
      <Seo title={t.checkout.title} description={t.checkout.redirect} path="/checkout" noindex />
      <div className="nw-container py-12">
        <h1 className="font-serif text-4xl text-nw-ivory mb-8">{t.checkout.title}</h1>
        <div data-testid="checkout-stepper" className="flex items-center gap-2 mb-10 max-w-2xl">
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs border ${i <= step ? 'border-nw-gold text-nw-gold' : 'border-nw-gold/25 text-nw-warmgrey'}`}>
                  {i < step ? <Check size={14} /> : i + 1}
                </div>
                <span className={`text-[10px] tracking-luxe uppercase hidden sm:block ${i <= step ? 'text-nw-ivory' : 'text-nw-warmgrey'}`}>{s}</span>
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-px ${i < step ? 'bg-nw-gold/50' : 'bg-nw-gold/15'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-12">
          <div>
            {customer && (
              <div data-testid="checkout-signed-in" className="mb-6 flex items-center gap-2 text-[11px] tracking-wide text-nw-warmgrey"><UserCheck size={14} className="text-nw-gold" /> {t.checkout.signedInAs} <span className="text-nw-ivory">{customer.email}</span></div>
            )}
            {step === 0 && (
              <div data-testid="checkout-step-customer-details-form" className="space-y-4 animate-fade-in">
                <h2 className="nw-eyebrow mb-4">{t.checkout.customer}</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className={label}>{t.checkout.firstName}</label><input data-testid="checkout-first-name" className={field} value={customerInfo.first_name} onChange={setC('first_name')} /></div>
                  <div><label className={label}>{t.checkout.lastName}</label><input data-testid="checkout-last-name" className={field} value={customerInfo.last_name} onChange={setC('last_name')} /></div>
                  <div><label className={label}>{t.checkout.email}</label><input data-testid="checkout-email" type="email" className={field} value={customerInfo.email} onChange={setC('email')} /></div>
                  <div><label className={label}>{t.checkout.phone}</label><input data-testid="checkout-phone" className={field} value={customerInfo.phone} onChange={setC('phone')} /></div>
                </div>
              </div>
            )}
            {step === 1 && (
              <div className="space-y-4 animate-fade-in">
                <h2 className="nw-eyebrow mb-4">{t.checkout.address}</h2>
                {customer?.addresses?.length > 0 && (
                  <div className="space-y-2 mb-6">
                    <div className={label}>{t.checkout.savedAddresses}</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {customer.addresses.map((a) => (
                        <button type="button" key={a.id} data-testid={`checkout-saved-address-${a.id}`} onClick={() => pickSaved(a)}
                          className={`text-left border rounded-[3px] p-4 text-sm transition-colors ${selectedAddr === a.id ? 'border-nw-gold bg-nw-espresso' : 'border-nw-gold/20 hover:border-nw-gold/50'}`}>
                          <div className="flex items-center gap-2 text-[10px] tracking-luxe uppercase text-nw-warmgrey"><MapPin size={12} className="text-nw-gold" /> {a.label || a.city}</div>
                          <div className="text-nw-ivory mt-1">{a.line1}</div>
                          <div className="text-nw-warmgrey text-xs">{a.postal_code} {a.city}, {a.country}</div>
                        </button>
                      ))}
                      <button type="button" data-testid="checkout-new-address" onClick={pickNew}
                        className={`text-left border rounded-[3px] p-4 text-sm transition-colors ${selectedAddr === 'new' ? 'border-nw-gold bg-nw-espresso' : 'border-nw-gold/20 hover:border-nw-gold/50'}`}>
                        <div className="text-[10px] tracking-luxe uppercase text-nw-warmgrey">+</div>
                        <div className="text-nw-ivory mt-1">{t.checkout.newAddress}</div>
                      </button>
                    </div>
                  </div>
                )}
                <div><label className={label}>{t.checkout.line1}</label><input data-testid="checkout-line1" className={field} value={address.line1} onChange={setA('line1')} /></div>
                <div><label className={label}>{t.checkout.line2}</label><input className={field} value={address.line2} onChange={setA('line2')} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className={label}>{t.checkout.city}</label><input data-testid="checkout-city" className={field} value={address.city} onChange={setA('city')} /></div>
                  <div><label className={label}>{t.checkout.postal}</label><input data-testid="checkout-postal" className={field} value={address.postal_code} onChange={setA('postal_code')} /></div>
                  <div><label className={label}>{t.checkout.country}</label><input data-testid="checkout-country" className={field} value={address.country} onChange={setA('country')} /></div>
                  <div><label className={label}>{t.checkout.state}</label><input className={field} value={address.state} onChange={setA('state')} /></div>
                </div>
                {(selectedAddr === 'new' || !customer?.addresses?.length) && (
                  <label className="flex items-center gap-3 text-sm text-nw-warmgrey pt-2">
                    <Switch data-testid="checkout-save-address-toggle" checked={saveAddress} onCheckedChange={setSaveAddress} className="data-[state=checked]:bg-nw-gold" /> {t.checkout.saveAddress}
                  </label>
                )}
              </div>
            )}
            {step === 2 && (
              <div className="space-y-6 animate-fade-in">
                <h2 className="nw-eyebrow mb-4">{t.checkout.options}</h2>
                <div className="space-y-3">
                  <div className={label}>{t.checkout.method}</div>
                  {Object.entries(methods).map(([key, m]) => (
                    <label key={key} data-testid={`checkout-shipping-${key}`} className={`flex items-center justify-between border rounded-[3px] px-4 py-3 cursor-pointer transition-colors ${shipping === key ? 'border-nw-gold' : 'border-nw-gold/20 hover:border-nw-gold/50'}`}>
                      <div className="flex items-center gap-3">
                        <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${shipping === key ? 'border-nw-gold' : 'border-nw-gold/40'}`}>{shipping === key && <span className="w-2 h-2 rounded-full bg-nw-gold" />}</span>
                        <div>
                          <div className="text-sm text-nw-ivory">{lang === 'fr' ? m.label_fr : m.label_en}</div>
                          <div className="text-[11px] text-nw-warmgrey">{m.days} {t.checkout.days}</div>
                        </div>
                      </div>
                      <input type="radio" name="ship" className="hidden" checked={shipping === key} onChange={() => setShipping(key)} />
                      <span className="text-nw-gold text-sm">{formatPrice(m.price)}</span>
                    </label>
                  ))}
                </div>
                <div className="flex items-center justify-between border border-nw-gold/20 rounded-[3px] px-4 py-4">
                  <div className="pr-4">
                    <div className="text-sm text-nw-ivory">{t.checkout.insurance}</div>
                    <div className="text-[11px] text-nw-warmgrey mt-1">{t.checkout.insuranceNote}</div>
                  </div>
                  <Switch data-testid="checkout-insurance-toggle" checked={insurance} onCheckedChange={setInsurance} className="data-[state=checked]:bg-nw-gold" />
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-5 animate-fade-in" data-testid="checkout-payment-step">
                <h2 className="nw-eyebrow mb-4">{steps[3]}</h2>
                <div className="border border-nw-gold/20 rounded-[3px] p-6">
                  <p className="text-sm text-nw-warmgrey leading-relaxed">{t.checkout.redirect}</p>
                  {!customer && <p className="mt-3 text-sm text-nw-gold/90 leading-relaxed">{t.checkout.accountRequired}</p>}
                  <div className="mt-4 text-xs text-nw-warmgrey/80">
                    <div>{customerInfo.first_name} {customerInfo.last_name} · {customerInfo.email}</div>
                    <div className="mt-1">{address.line1}, {address.postal_code} {address.city}, {address.country}</div>
                  </div>
                  <div className="mt-4 text-[11px] text-nw-gold/80">Test card: 4242 4242 4242 4242 — any future date — any CVC</div>
                </div>
                <button data-testid="checkout-pay-now-button" disabled={submitting} onClick={pay} className="nw-btn-gold w-full h-12">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> …</> : <>{t.checkout.pay}</>}
                </button>
              </div>
            )}

            <div className="flex items-center justify-between mt-8">
              {step > 0 ? <button data-testid="checkout-back" onClick={() => setStep((s) => s - 1)} className="text-[11px] tracking-luxe uppercase nw-link">← {t.checkout.back}</button> : <Link to="/cart" className="text-[11px] tracking-luxe uppercase nw-link">← {t.cart.title}</Link>}
              {step < 3 && <button data-testid="checkout-next" disabled={!canNext()} onClick={() => canNext() && setStep((s) => s + 1)} className={`nw-btn-gold ${!canNext() ? 'opacity-40 pointer-events-none' : ''}`}>{t.checkout.next}</button>}
            </div>
          </div>

          <div className="mt-10 lg:mt-0">
            <div className="bg-nw-espresso border border-nw-gold/20 p-6 lg:sticky lg:top-[92px]">
              <h2 className="nw-eyebrow mb-5">{t.checkout.summary}</h2>
              <div data-testid="checkout-summary-items" className="space-y-3 max-h-64 overflow-y-auto">
                {cart.map((i) => (
                  <div key={i.product_id} data-testid={`checkout-summary-item-${i.reference_id}`} className="flex gap-3">
                    <div className="w-12 h-16 bg-nw-black shrink-0 overflow-hidden border border-nw-gold/15">{i.image && <img src={i.image} alt={i.name} className="w-full h-full object-cover" />}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-nw-ivory truncate">{i.name}</div>
                      <div className="text-[11px] text-nw-warmgrey">× {i.quantity}</div>
                    </div>
                    <div className="text-xs text-nw-gold">{formatPrice(i.price_eur * i.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-nw-gold/15 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-nw-warmgrey">{t.cart.subtotal}</span><span className="text-nw-ivory">{formatPrice(pricing?.subtotal_eur ?? cartSubtotal)}</span></div>
                <div className="flex justify-between"><span className="text-nw-warmgrey">{t.cart.shipping}</span><span className="text-nw-ivory">{formatPrice(pricing?.shipping_eur ?? 0)}</span></div>
                {insurance && <div className="flex justify-between"><span className="text-nw-warmgrey">{t.cart.insuranceLabel}</span><span className="text-nw-ivory">{formatPrice(pricing?.insurance_eur ?? 0)}</span></div>}
                <div className="flex justify-between text-[12px]"><span className="text-nw-warmgrey">{t.cart.vatOf} ({(pricing?.vat_rate ?? 20)}%)</span><span className="text-nw-warmgrey">{formatPrice(pricing?.vat_eur ?? 0)}</span></div>
              </div>
              <div className="flex justify-between items-baseline mt-4 pt-4 border-t border-nw-gold/25">
                <span className="text-[11px] tracking-luxe uppercase text-nw-warmgrey">{t.cart.grandTotal}</span>
                <span data-testid="checkout-total" className="font-serif text-2xl text-nw-gold">{formatPrice(pricing?.total_eur ?? cartSubtotal)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} defaultMode="signup" intro={t.checkout.accountRequired}
        onAuthenticated={() => { toast.success(t.account.created); setTimeout(launchPayment, 150); }} />
    </div>
  );
}
